import React from 'react';
import { Box, Popover, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useUserAuth from '../../../../utils/useUserAuth';
import { formatSportTypes } from './sportTypeLabel';
import AccessDoorRow from './AccessDoorRow';
import { useAutoDeductionVisible } from '../modals/AutoDeductionSettingModal';

import {
  TicketCard,
  ExpiredCardWrapper,
  CardInfoSection,
  HeaderRow,
  HeaderLeft,
  ClassTypeLabel,
  TicketTitle,
  TypeLabel,
  InfoRow,
  InfoLeft,
  InfoLabel,
  InfoValue,
  CountGroup,
  CountCurrent,
  CountTotal,
  HighlightText,
  ExpiredLabel,
  UpcomingLabel,
  CardButtonAreaColumn,
  CardButtonRow,
  CardButton,
} from './CourseTicketCardStyled';
import { usageBranchLabel, canEditUseBranches } from '../../../../utils/branchShare';
import { toActionRows } from './cardActions';

const URGENT_DAYS_THRESHOLD = 10;

const CourseTicketCard = ({ tick, branchTrainerIds = [], isExpired, focused, onPeriodChange, onFreeze, onInstructorChange, onReservationSetting, onBatchReservation, onHistoryOpen, onUseBranchEdit, onChangeHistoryOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sharedAnchor, setSharedAnchor] = React.useState(null);
  const canPeriodChange = useUserAuth('MEMBER_PASS_PERIOD_CHANGE');
  const canSuspend = useUserAuth('MEMBER_PASS_SUSPEND');
  const canBatchReserve = useUserAuth('MEMBER_LESSON_BATCH_RESERVE');
  const canReserveSetting = useUserAuth('MEMBER_COURSE_RESERVE_SETTING');
  const autoDeductionVisible = useAutoDeductionVisible();
  const isStopScheduled = tick.displayStatus === 'STOP_SCHEDULED' || (tick.status === 'ACTIVE' && tick.stoppedDate && dayjs(tick.stoppedDate).startOf('day').isAfter(dayjs().startOf('day')));
  const isStopped = tick.status === 'STOPPED' && !isStopScheduled;

  // 담당강사 표시 — 지점 강사 전원이면 '전체 강사', 여러 명이면 '첫 강사 외 N명'(전체 이름은 툴팁)
  const trainerNames = (tick.trainerName || '').split(', ').filter(Boolean);
  const assignedTrainerIds = tick.trainerIds || [];
  const isAllTrainers =
    branchTrainerIds.length > 1 && branchTrainerIds.every(id => assignedTrainerIds.includes(id));
  const trainerSummary = isAllTrainers
    ? '전체 강사'
    : trainerNames.length > 1
      ? `${trainerNames[0]} 외 ${trainerNames.length - 1}명`
      : trainerNames[0] || '-';

  // 잔여 정보 계산. 차감은 출결 확정 시점이라 remainingCount에는 예약중 건수가 빠져 있다 —
  // reservedCount(백엔드 배치 집계)를 함께 보여줘 초과 예약 오인을 막는다.
  const totalCount = Number(tick.usageCount) || 0;
  const usedCount = Number(tick.usedCount) || 0;
  const remainingCount = Math.max(totalCount - usedCount, 0);
  const reservedCount = Number(tick.reservedCount) || 0;
  const myReservedCount = tick.myReservedCount == null ? null : Number(tick.myReservedCount) || 0;

  // 시작일이 미래면 '사용예정'으로 표시하고 카운트하지 않는다.
  // 시작일이 지난 경우에만 오늘 기준 남은 일수를 계산한다.
  const today = dayjs().startOf('day');
  const isUpcoming = tick.issueDate ? today.isBefore(dayjs(tick.issueDate).startOf('day')) : false;

  const daysRemaining =
    !isUpcoming && tick.expireDate ? dayjs(tick.expireDate).startOf('day').diff(today, 'day') : null;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= URGENT_DAYS_THRESHOLD;

  const formatDateRange = () =>
    `${tick.issueDate?.replaceAll('-', '.')} ~ ${tick.expireDate?.replaceAll('-', '.')}`;

  const periodText =
    tick.usagePeriodType === 'MONTH' ? `${tick.usagePeriod}개월` : `${tick.usagePeriod}일`;

  const sportTypeText = formatSportTypes(tick.sportTypes);

  // 수강권 공유(#154) — 공유받은 카드에서도 정보 수정·중지·강사·자동차감·이용지점 같은 수정은 소유자 카드와 같이 된다
  // (2026-10-06 확정). 공유 설정 탭과 돈 관련(양도·환불·회원권 변경)만 소유자 전용이다.
  const isSharedFrom = !!tick.sharedFrom;
  const sharedMembers = tick.sharedMembers ?? [];
  const isShared = isSharedFrom || sharedMembers.length > 0;
  // 공유가 해제된 카드(#172) — 만료 목록에 읽기 전용으로만 보인다(이 지점 회원인 이유 표시용)
  const isShareEnded = !!tick.shareEnded;

  // 공유 상대 회원의 상품(수강권) 탭으로 이동해 같은 회원권 카드로 스크롤·강조한다(?ticketId=).
  const goToMember = memberId => navigate(`/member/${memberId}?tab=three&ticketId=${tick.memberCourseTicketId}`);
  const linkSx = { color: '#0F7F85', textDecoration: 'underline', cursor: 'pointer', '&:hover': { color: '#0B6368' } };
  const phoneTail = phone => (phone ? String(phone).replace(/\D/g, '').slice(-4) : '');
  const renderMemberLink = member => (
    <Box
      component="a"
      href={`/member/${member.memberId}?tab=three&ticketId=${tick.memberCourseTicketId}`}
      onClick={event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return; // 새 탭 열기는 브라우저에 맡긴다
        event.preventDefault();
        goToMember(member.memberId);
      }}
      sx={linkSx}>
      {member.name}
    </Box>
  );

  const usageLabel = usageBranchLabel(tick.branchShare, { forMember: true });
  const showUseBranchEdit = !isExpired && !!onUseBranchEdit && canEditUseBranches(tick.branchShare);

  // 하단 액션 버튼 — 조건에 맞는 것만 모아 2열로 배치한다(홀수면 마지막이 전폭).
  const allActions = [
    {
      key: 'period',
      label: '상품 정보 수정',
      onClick: canPeriodChange ? onPeriodChange : undefined,
      tooltip: !canPeriodChange ? '권한이 없습니다' : '',
      dimmed: !canPeriodChange,
    },
    ...(tick?.status !== 'INACTIVE' ? [{
      key: 'stop',
      label: '수강권 중지 설정',
      onClick: canSuspend ? onFreeze : undefined,
      tooltip: !canSuspend ? '권한이 없습니다' : '',
      dimmed: !canSuspend,
      disabled: isExpired && !tick?.isPossibleStop,
    }] : []),
    ...(!isExpired && tick?.classType !== 'GROUP_LESSON' ? [{
      key: 'instructor',
      label: '담당 강사 변경',
      onClick: onInstructorChange,
      disabled:
        tick?.classType !== 'PRIVATE_LESSON' && tick?.classType !== 'DUET_LESSON' && tick?.classType !== 'OT',
    }] : []),
    ...(!isExpired ? [{
      key: 'batchReserve',
      label: '레슨 일괄예약',
      onClick: canBatchReserve ? onBatchReservation : undefined,
      tooltip: !canBatchReserve ? '권한이 없습니다' : '',
      dimmed: !canBatchReserve,
    }] : []),
    ...(showUseBranchEdit ? [{
      key: 'useBranch',
      label: '이용지점 변경',
      onClick: onUseBranchEdit,
    }] : []),
    ...(onChangeHistoryOpen ? [{
      key: 'changeHistory',
      label: '변경 내역 조회',
      onClick: onChangeHistoryOpen,
    }] : []),
  ];
  const actionRows = toActionRows(isShareEnded ? [] : allActions);

  const card = (
    <TicketCard
      id={`member-course-ticket-${tick.memberCourseTicketId}`}
      isStopped={!isExpired && isStopped}
      // ?ticketId= 로 진입한 카드 — 링을 두 번 깜빡여 눈에 띄게 한 뒤 유지
      sx={{
        // 공유 상품 — 브랜드 색 테두리(2px, 1px 늘어난 만큼 패딩을 줄여 크기 유지)
        ...(isShared && { border: '2px solid #4AB3BC', padding: '15px', position: 'relative' }),
        ...(focused && {
          boxShadow: '0 0 0 3px rgba(74,179,188,0.45)',
          animation: 'memberTicketFocusPulse 1.2s ease-in-out 2',
          '@keyframes memberTicketFocusPulse': {
            '0%, 100%': { boxShadow: '0 0 0 3px rgba(74,179,188,0.45)' },
            '50%': { boxShadow: '0 0 0 9px rgba(74,179,188,0.12)' },
          },
        }),
      }}>
      {isShared && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            border: '1.5px solid #4AB3BC',
            borderRadius: '999px',
            padding: '0 10px',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '20px',
            color: '#0F7F85',
            whiteSpace: 'nowrap',
          }}>
          {isShareEnded ? '공유 해제됨' : '공유 상품'}
        </Box>
      )}
      <CardInfoSection>
        {/* Row 1: 카테고리 + 상품명 | 종류 라벨 */}
        <HeaderRow>
          <HeaderLeft>
            <ClassTypeLabel isStopped={!isExpired && isStopped}>{t(tick.classType)}</ClassTypeLabel>
            <Tooltip title={`${tick.ticketName} ${tick.usageCount}회 ${periodText}`} arrow componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
              <TicketTitle isStopped={!isExpired && isStopped}>
                {tick.ticketName} {tick.usageCount}회 {periodText}
              </TicketTitle>
            </Tooltip>
          </HeaderLeft>
          {/* 상품종류(수강권) 라벨은 섹션 제목과 중복이라 제거하고, 중지 상태만 남긴다. */}
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {!isExpired && (isStopScheduled || isStopped) && (
              <TypeLabel isStopped={isStopped} sx={isStopScheduled ? { color: '#EE735A' } : {}}>
                {isStopScheduled ? `중지 예정 (${dayjs(tick.stoppedDate).format('M/D')}~)` : '중지'}
              </TypeLabel>
            )}
            {onHistoryOpen && (
              <Box
                component="button"
                type="button"
                onClick={onHistoryOpen}
                sx={{
                  border: '1px solid #E0E4EA',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  color: '#5C6470',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: '0 9px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 150ms ease',
                  '&:hover': { backgroundColor: '#F6F9FE', borderColor: '#B9CBF2', color: '#1976d2' },
                }}>
                예약 내역
              </Box>
            )}
          </Stack>
        </HeaderRow>

        {/* Row 1.5: 다지점 공유 상품 이용지점(#98) — 비공유면 빈 문자열이라 안 그린다 */}
        {usageLabel && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>이용지점</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{usageLabel}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 1.7: 수강권 공유(#154) — 소유자 카드는 공유 회원, 공유받은 카드는 소유자 */}
        {isSharedFrom && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>공유한 회원</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{renderMemberLink(tick.sharedFrom)}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}
        {!isSharedFrom && sharedMembers.length > 0 && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>공유 회원</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>
                {sharedMembers.length === 1 ? (
                  renderMemberLink(sharedMembers[0])
                ) : (
                  <>
                    <Box
                      component="button"
                      type="button"
                      aria-haspopup="true"
                      aria-expanded={!!sharedAnchor}
                      onClick={event => setSharedAnchor(event.currentTarget)}
                      sx={{ ...linkSx, background: 'none', border: 0, padding: 0, font: 'inherit' }}>
                      {sharedMembers[0].name} 외 {sharedMembers.length - 1}
                    </Box>
                    <Popover
                      open={!!sharedAnchor}
                      anchorEl={sharedAnchor}
                      onClose={() => setSharedAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                      <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: '8px 4px', minWidth: 160, maxHeight: 280, overflowY: 'auto' }}>
                        {sharedMembers.map(m => (
                          <Box component="li" key={m.memberId} sx={{ px: '12px', py: '6px', fontSize: 13 }}>
                            <Box
                              component="a"
                              href={`/member/${m.memberId}?tab=three&ticketId=${tick.memberCourseTicketId}`}
                              onClick={event => {
                                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                                event.preventDefault();
                                setSharedAnchor(null);
                                goToMember(m.memberId);
                              }}
                              sx={linkSx}>
                              {m.name}
                            </Box>
                            {phoneTail(m.phoneNumber) && <Box component="span" sx={{ ml: '6px', color: '#888' }}>({phoneTail(m.phoneNumber)})</Box>}
                          </Box>
                        ))}
                      </Stack>
                    </Popover>
                  </>
                )}
              </InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 2: 운동종목 (다종목 상품은 쉼표로 나열) */}
        {sportTypeText && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>종목</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{sportTypeText}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 2.5: 출입문 (전환 지점만 렌더 — #91 회원별 오버라이드) */}
        <AccessDoorRow
          holdingType="TICKET"
          holdingId={tick.memberCourseTicketId}
          isStopped={!isExpired && isStopped}
        />

        {/* Row 3: 담당강사 (개인레슨/듀엣레슨만 — 그룹레슨은 수업별 강사이므로 수강권 담당강사 미표시) */}
        {tick.classType !== 'GROUP_LESSON' && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>담당강사</InfoLabel>
              <Tooltip title={trainerNames.length > 1 ? tick.trainerName : ''} placement="top" arrow>
                <InfoValue isStopped={!isExpired && isStopped}>{trainerSummary}</InfoValue>
              </Tooltip>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 4: 잔여횟수 */}
        <InfoRow>
          <InfoLeft>
            <InfoLabel isStopped={!isExpired && isStopped}>잔여횟수</InfoLabel>
            <CountGroup>
              <CountCurrent isStopped={!isExpired && isStopped}>{remainingCount}회</CountCurrent>
              <CountTotal isStopped={!isExpired && isStopped}>/</CountTotal>
              <CountTotal isStopped={!isExpired && isStopped}>{totalCount}회</CountTotal>
            </CountGroup>
          </InfoLeft>
          {!isExpired && <HighlightText isStopped={isStopped}>{remainingCount}회 남음</HighlightText>}
        </InfoRow>

        {/* Row 4.2: 차감 예정(예약) */}
        {!isExpired && reservedCount > 0 && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={isStopped} sx={{ whiteSpace: 'nowrap' }}>예약(차감 예정 횟수)</InfoLabel>
            </InfoLeft>
            <HighlightText isStopped={isStopped}>
              {isShared && myReservedCount != null &&
                `(본인 ${myReservedCount} · 공유 ${Math.max(reservedCount - myReservedCount, 0)}) `}
              {reservedCount}회 예정
            </HighlightText>
          </InfoRow>
        )}

        {/* Row 4.5: 자동 차감 (설정된 수강권만, 기업회원 자동 차감 설정이 꺼진 곳은 숨김) */}
        {autoDeductionVisible && tick.autoDeductionCount != null && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>자동차감</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>
                매월 {tick.autoDeductionAnchorDay}일 {tick.autoDeductionCount}회
                {!isExpired && tick.nextAutoDeductionDate
                  ? ` · 다음 ${dayjs(tick.nextAutoDeductionDate).format('M.D')}`
                  : ''}
              </InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 5: 유효기간 + 잔여일수 */}
        <InfoRow>
          <InfoLeft>
            <InfoLabel isStopped={!isExpired && isStopped}>유효기간</InfoLabel>
            <InfoValue isStopped={!isExpired && isStopped}>{formatDateRange()}</InfoValue>
          </InfoLeft>
          {isExpired ? (
            <ExpiredLabel>만료</ExpiredLabel>
          ) : isUpcoming ? (
            <UpcomingLabel>사용예정</UpcomingLabel>
          ) : (
            daysRemaining !== null &&
            daysRemaining >= 0 && (
              <HighlightText isStopped={isStopped} urgent={isUrgent}>
                {daysRemaining}일 남음
              </HighlightText>
            )
          )}
        </InfoRow>
      </CardInfoSection>

      <CardButtonAreaColumn isStopped={!isExpired && isStopped}>
        {actionRows.map((row, rowIndex) => (
          <CardButtonRow key={row.map(action => action.key).join('-')}>
            {row.map((action, index) => (
              <Tooltip key={action.key} title={action.tooltip ?? ''} arrow>
                <CardButton
                  variant="text"
                  isStopped={!isExpired && isStopped}
                  noBorderTop={rowIndex > 0}
                  noBorderRight={row.length === 2 && index === 0}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  style={action.dimmed ? { opacity: 0.5, cursor: 'default' } : {}}>
                  {action.label}
                </CardButton>
              </Tooltip>
            ))}
          </CardButtonRow>
        ))}
      </CardButtonAreaColumn>
    </TicketCard>
  );

  if (isExpired) {
    return <ExpiredCardWrapper sx={isShared ? { pt: '10px' } : undefined}>{card}</ExpiredCardWrapper>;
  }

  // 공유 상품 라벨이 테두리 위로 반쯤 걸치므로 위 카드와 겹치지 않게 위쪽 여백을 둔다
  return <Box sx={isShared ? { pt: '10px' } : undefined}>{card}</Box>;
};

export default CourseTicketCard;
